import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StyledNavigationSwiper, SelectionOption } from '../StyledNavigationSwiper';
import {
  tournamentDetailRoutePattern,
  matchDetailRoutePattern,
  participantStatsRoutePattern,
} from '@/utils/routePatterns';
import { leagueTabPath, useLeagueTabs } from '@/hooks/useLeagueTabs';
import type { Swiper as SwiperType } from 'swiper';

export interface RouterNavigationSwiperProps {
  innerRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  controlledSwiper?: SwiperType | null;
  onSwiper?: (swiper: SwiperType) => void;
  hiddenPaths?: string[];
}

/**
 * RouterNavigationSwiper - Navigation component integrated with React Router
 *
 * Automatically determines navigation options and active state based on current route.
 * Uses useLocation and useNavigate hooks internally.
 * Wraps StyledNavigationSwiper for route-aware navigation.
 */
export const RouterNavigationSwiper: React.FC<RouterNavigationSwiperProps> = ({
  innerRef,
  className,
  controlledSwiper,
  onSwiper,
  hiddenPaths,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ id?: string; tournamentId?: string; matchId?: string; userId?: string }>();
  const { t } = useTranslation(['common', 'tournament', 'account', 'admin', 'league', 'match']);

  // Check if we're on a per-participant statistics page
  const isParticipantStatsPage = useMemo(() => {
    return participantStatsRoutePattern.test(location.pathname);
  }, [location.pathname]);

  // Check if we're on a match detail page
  const isMatchDetailPage = useMemo(() => {
    return matchDetailRoutePattern.test(location.pathname);
  }, [location.pathname]);

  // Check if we're on a tournament detail page (but not match or participant-stats page)
  const isTournamentDetailPage = useMemo(() => {
    return (
      tournamentDetailRoutePattern.test(location.pathname) &&
      !isMatchDetailPage &&
      !isParticipantStatsPage
    );
  }, [location.pathname, isMatchDetailPage, isParticipantStatsPage]);

  // Extract tournament ID, match ID, and participant user ID from route params or pathname
  const { tournamentId, matchId, userId } = useMemo(() => {
    // First check route params
    let tId = params.id || params.tournamentId || null;
    let mId = params.matchId || null;
    let uId = params.userId || null;

    // Fallback: extract from pathname using regex for match pages
    if (!tId || !mId) {
      const matchMatch = location.pathname.match(matchDetailRoutePattern);
      if (matchMatch) {
        tId = tId || matchMatch[1];
        mId = mId || matchMatch[2];
      }
    }

    // Fallback: extract from pathname for participant-stats pages
    if (!tId || !uId) {
      const statsMatch = location.pathname.match(participantStatsRoutePattern);
      if (statsMatch) {
        tId = tId || statsMatch[1];
        uId = uId || statsMatch[2];
      }
    }

    // Fallback: extract tournament ID for tournament pages
    if (!tId) {
      const tournamentMatch = location.pathname.match(tournamentDetailRoutePattern);
      if (tournamentMatch) {
        tId = tournamentMatch[1];
      }
    }

    return { tournamentId: tId, matchId: mId, userId: uId };
  }, [params.id, params.tournamentId, params.matchId, params.userId, location.pathname]);

  // League detail pages: the tabs depend on what is stored for the league.
  const leagueId = location.pathname.match(/^\/matches\/(\d+)(?:\/|$)/)?.[1] ?? '';
  const { tabs: leagueTabs } = useLeagueTabs(leagueId);

  // Determine navigation options based on current path
  const options = useMemo((): SelectionOption[] => {
    const path = location.pathname;

    // Admin pages
    if (path.startsWith('/admin')) {
      return [
        { label: t('admin:users'), value: '/admin/users' },
        { label: t('admin:bpSettings'), value: '/admin/bp-settings' },
        { label: t('admin:leagues'), value: '/admin/leagues' },
        { label: t('admin:bookmakers'), value: '/admin/bookmakers' },
        { label: t('admin:leagueBookmakers'), value: '/admin/league-bookmakers' },
        { label: t('admin:matchesAdmin'), value: '/admin/matches' },
        { label: t('admin:debugApi'), value: '/admin/debug-api' },
      ];
    }

    // Account pages
    if (path.startsWith('/account')) {
      return [
        { label: t('account:editTab'), value: '/account/edit' },
        { label: t('account:messages'), value: '/account/messages' },
        { label: t('account:security'), value: '/account/security' },
      ];
    }

    // Match detail pages
    if (isMatchDetailPage && tournamentId && matchId) {
      return [
        { label: t('tournament:match'), value: `/tournaments/${tournamentId}/matches/${matchId}` },
        { label: t('tournament:predictions'), value: `/tournaments/${tournamentId}/matches/${matchId}/predictions` },
      ];
    }

    // Per-participant statistics pages — single "Статистика" tab for now;
    // the array shape is preserved so future tabs can be appended here
    // without restructuring this branch. Checked BEFORE tournament-detail so
    // the participant-scoped tabs win.
    if (isParticipantStatsPage && tournamentId && userId) {
      return [
        { label: t('tournament:participantStats.tabs.stats'), value: `/tournaments/${tournamentId}/leaderboard/${userId}` },
      ];
    }

    // Tournament detail pages
    if (isTournamentDetailPage && tournamentId) {
      return [
        { label: t('tournament:matches'), value: `/tournaments/${tournamentId}` },
        { label: t('tournament:participants'), value: `/tournaments/${tournamentId}/participants` },
        { label: t('tournament:leaderboard'), value: `/tournaments/${tournamentId}/leaderboard` },
        { label: t('tournament:settings'), value: `/tournaments/${tournamentId}/settings` },
      ];
    }

    // Standalone match page — mirrors MatchOverviewLayout's `tabs` array.
    // `/match/` never prefixes `/matches` (and vice versa), so the league
    // browser branch below cannot catch it.
    const standaloneMatch = path.match(/^\/match\/(\d+)(?:\/|$)/);
    if (standaloneMatch) {
      return [
        { label: t('match:tabOverview'), value: `/match/${standaloneMatch[1]}` },
        { label: t('match:tabTournaments'), value: `/match/${standaloneMatch[1]}/tournaments` },
      ];
    }

    // League browser pages - the detail level takes the list LeagueDetailLayout
    // swipes through, so the nav strip and the mobile swipe sequence stay identical.
    if (path.startsWith('/matches')) {
      if (leagueId) {
        return leagueTabs.map(tab => ({ label: t(`league:${tab}`), value: leagueTabPath(leagueId, tab) }));
      }
      return [
        { label: t('league:matches'), value: '/matches' },
        { label: t('league:leagues'), value: '/matches/leagues' },
      ];
    }

    // Tournament list pages
    if (path.startsWith('/tournaments')) {
      return [
        { label: t('tournament:tournaments'), value: '/tournaments' },
        { label: t('tournament:publicTournaments'), value: '/tournaments/public' },
        { label: t('tournament:archived'), value: '/tournaments/archived' },
      ];
    }

    return [
      { label: t('common:home'), value: '/dashboard' },
      { label: t('tournament:tournaments'), value: '/tournaments' },
      { label: t('tournament:publicTournaments'), value: '/tournaments/public' },
      { label: t('tournament:archived'), value: '/tournaments/archived' },
      { label: t('account:editTab'), value: '/account' },
    ];
  }, [location.pathname, isMatchDetailPage, isTournamentDetailPage, isParticipantStatsPage, tournamentId, matchId, userId, leagueId, leagueTabs, t]);

  // Filter out hidden paths (e.g., Participants tab for unauthorized users)
  const filteredOptions = useMemo(() => {
    if (!hiddenPaths || hiddenPaths.length === 0) return options;
    return options.filter(opt => !hiddenPaths.includes(String(opt.value)));
  }, [options, hiddenPaths]);

  /**
   * Determines active option based on current path.
   * Matches options in order of specificity (longer paths first).
   */
  const getActiveValue = (): string | number => {
    const path = location.pathname;

    // Sort options by path length (descending) to match most specific first
    const sortedOptions = [...filteredOptions].sort((a, b) => {
      const aPath = String(a.value);
      const bPath = String(b.value);
      return bPath.length - aPath.length;
    });

    // Find first matching option
    for (const option of sortedOptions) {
      const optionPath = String(option.value);
      if (optionPath === '/') {
        // Root path - exact match only
        if (path === '/') return option.value;
      } else if (path.startsWith(optionPath)) {
        return option.value;
      }
    }

    // Default to first option if no match
    return filteredOptions[0]?.value ?? '/';
  };

  const handleNavigate = (value: string | number) => {
    navigate(String(value));
  };

  return (
    <StyledNavigationSwiper
      options={filteredOptions}
      active={getActiveValue()}
      setActive={handleNavigate}
      innerRef={innerRef}
      className={className}
      controlledSwiper={controlledSwiper}
      onSwiper={onSwiper}
    />
  );
};

export default RouterNavigationSwiper;
